import { formatDistanceToNow } from "date-fns";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { UserRole } from "@features/settings/types/settings.types";
import { ConfirmationModal } from "@shared/components/ConfirmationModal";
import { getProfileImage, useProfile } from "@shared/hooks/useProfile";
import theme from "@shared/utils/themeColors";
import { toast } from "react-hot-toast";
import { useSpacesStore } from "../store/useSpacesStore";
import { SpaceComment } from "../types/space.types";
import CommentForm from "./CommentForm";

export interface CommentItemProps {
  comment: SpaceComment;
  onReply: (commentId?: string) => void;
  isReplyActive: boolean;
  onMention: (username: string) => void;
  getCommentsByParentId: (parentId?: string) => SpaceComment[];
  getLimitedComments: (parentId?: string) => SpaceComment[];
  hasMoreComments: (parentId?: string) => boolean;
  loadMoreComments: (parentId?: string) => void;
  nestingLevel?: number; // Track the nesting level of comments
}

function CommentItem({
  comment,
  onReply,
  isReplyActive,
  onMention,
  getCommentsByParentId,
  getLimitedComments,
  hasMoreComments,
  loadMoreComments,
  nestingLevel = 0, // Default to 0 for top-level comments
}: CommentItemProps) {
  const { toggleCommentUpvote, addComment, deleteComment } = useSpacesStore();
  const { user, profile } = useProfile();

  // State for upvote count (initially from the comment's upvotes)
  const [upvotes, setUpvotes] = useState(comment.upvotes || 0);
  const [isUpvoted, setIsUpvoted] = useState(comment.isUpvoted || false);
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [replyContent, setReplyContent] = useState("");
  const [isSubmittingReply, setIsSubmittingReply] = useState(false);

  // Delete confirmation modal
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Check if user can delete this comment (author or admin)
  const canDelete =
    profile &&
    ((comment.author && profile.id === comment.author.id) ||
      user?.role === UserRole.ADMIN);

  // Get all users who have commented on this space for mentions
  const commenters = useMemo(() => {
    // Get unique commenters from all comments
    return getCommentsByParentId(undefined)
      .map((comment) => comment.author)
      .filter(
        (author, index, self) =>
          index === self.findIndex((a) => a.id === author.id)
      );
  }, [getCommentsByParentId]);

  // Handle upvote click
  const handleUpvote = async () => {
    if (!comment.id) return;

    try {
      // Optimistic update
      if (isUpvoted) {
        setUpvotes((prev) => Math.max(0, prev - 1));
      } else {
        setUpvotes((prev) => prev + 1);
      }
      setIsUpvoted(!isUpvoted);

      // Call API to update the upvote
      const result = await toggleCommentUpvote(comment.id);

      // If API call fails, revert the optimistic update
      if (!result.success) {
        if (isUpvoted) {
          setUpvotes((prev) => prev + 1);
        } else {
          setUpvotes((prev) => Math.max(0, prev - 1));
        }
        setIsUpvoted(isUpvoted);
      }
    } catch (error) {
      console.error("Error toggling comment upvote:", error);
      // Revert optimistic update on error
      if (isUpvoted) {
        setUpvotes((prev) => prev + 1);
      } else {
        setUpvotes((prev) => Math.max(0, prev - 1));
      }
      setIsUpvoted(isUpvoted);
    }
  };

  // Handle reply button click
  const handleReplyClick = () => {
    setShowReplyForm(true);
    // Notify parent component for mobile view handling
    if (comment.id) {
      onReply(comment.id);
    }
  };

  // Check if we should allow replies at this level
  // Limit to 3 levels of nesting (0, 1, 2)
  const canReply = nestingLevel < 3;

  // Handle reply submission
  const handleReplySubmit = async () => {
    if (!comment.id || !comment.spaceId || !replyContent.trim()) return;

    try {
      setIsSubmittingReply(true);
      const newComment = await addComment(
        comment.spaceId,
        replyContent,
        comment.id
      );

      // Clear form and hide it on success
      if (newComment) {
        setReplyContent("");
        setShowReplyForm(false);
        // Reset the active reply in parent component
        onReply(undefined);

        // Scroll to the new comment after a short delay to ensure it's rendered
        if (newComment.id) {
          setTimeout(() => {
            const commentElement = document.getElementById(
              `comment-${newComment.id}`
            );
            if (commentElement) {
              commentElement.scrollIntoView({
                behavior: "smooth",
                block: "center",
              });
              // Add a brief highlight effect
              commentElement.classList.add(`${theme.bg.accent}`);
              setTimeout(() => {
                commentElement.classList.remove(`${theme.bg.accent}`);
              }, 2000);
            }
          }, 300); // Slightly longer delay for nested comments
        }
      }
    } catch (error) {
      console.error("Error adding reply:", error);
    } finally {
      setIsSubmittingReply(false);
    }
  };

  // Handle reply cancellation
  const handleCancelReply = () => {
    setShowReplyForm(false);
    setReplyContent("");
    // Reset the active reply in parent component
    onReply(undefined);
  };

  // Handle delete button click
  const handleDeleteClick = () => {
    setShowDeleteConfirmation(true);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!comment.id || !comment.spaceId) return;

    try {
      setIsDeleting(true);
      const result = await deleteComment(comment.spaceId, comment.id);

      if (result.success) {
        toast.success("Comment deleted successfully");
        setShowDeleteConfirmation(false);
      } else {
        toast.error("Failed to delete comment");
      }
    } catch (error) {
      console.error("Error deleting comment:", error);
      toast.error("Failed to delete comment");
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle delete cancellation
  const handleDeleteCancel = () => {
    setShowDeleteConfirmation(false);
  };

  return (
    <>
      <li className="relative pl-9 space-y-5">
        {/* Comment wrapper */}
        <div className="flex items-start">
          {/* Comment upvote */}
          <div className="absolute top-0 left-0">
            <button
              className={`text-xs font-semibold text-left w-6 rounded-sm flex flex-col justify-center items-center ${
                isUpvoted
                  ? `${theme.text.brand}`
                  : `${theme.text.secondary} hover:${theme.text.brand}`
              }`}
              onClick={handleUpvote}
            >
              <svg
                className={`inline-flex ${
                  isUpvoted ? theme.bg.fill : "fill-gray-400 dark:fill-gray-500"
                } mt-1.5 mb-1.5`}
                width="12"
                height="6"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="m0 6 6-6 6 6z" />
              </svg>
              <div>{upvotes}</div>
            </button>
          </div>

          {/* Comment content */}
          <div>
            {/* Comment text */}
            <div
              className={`grow text-sm ${theme.text.primary} space-y-2 mb-2`}
            >
              <p>{comment.content}</p>
            </div>

            {/* Comment footer */}
            <div className="flex flex-wrap text-xs">
              <div className="flex items-center after:block after:content-['·'] last:after:content-[''] after:text-sm after:text-gray-400 dark:after:text-gray-600 after:px-2">
                <Link
                  className="block mr-2"
                  to={`/community/profile/${comment.author.id}`}
                >
                  <img
                    className="rounded-full h-8 w-8"
                    src={getProfileImage(comment.author)}
                    alt={comment.author.name || "User"}
                  />
                </Link>
                <Link
                  className={`font-medium ${theme.text.brand} ${theme.text.brandHover}`}
                  to={`/community/profile/${comment.author.id}`}
                >
                  {comment.author.name || "Anonymous"}
                </Link>
              </div>
              {canReply && (
                <div
                  className={`flex items-center after:block after:content-['·'] last:after:content-[''] after:text-sm after:${theme.text.inactive} after:px-2`}
                >
                  <button
                    className={`font-medium ${theme.text.muted} ${theme.text.mutedHover}`}
                    onClick={handleReplyClick}
                  >
                    Reply
                  </button>
                </div>
              )}
              {canDelete && (
                <div
                  className={`flex items-center after:block after:content-['·'] last:after:content-[''] after:text-sm after:${theme.text.inactive} after:px-2`}
                >
                  <button
                    className={`font-medium text-red-500 hover:text-red-600`}
                    onClick={handleDeleteClick}
                    disabled={isDeleting}
                  >
                    {isDeleting ? "Deleting..." : "Delete"}
                  </button>
                </div>
              )}
              <div className="flex items-center">
                <span className={`${theme.text.muted}`}>
                  {comment.createdAt instanceof Date
                    ? formatDistanceToNow(comment.createdAt, {
                        addSuffix: true,
                      })
                    : formatDistanceToNow(
                        new Date(comment.createdAt?.toDate()),
                        {
                          addSuffix: true,
                        }
                      )}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Inline reply form */}
        {showReplyForm && (
          <div className="mt-3 ml-6">
            <CommentForm
              profile={profile}
              commentContent={replyContent}
              setCommentContent={setReplyContent}
              onSubmit={handleReplySubmit}
              isSubmitting={isSubmittingReply}
              parentComment={comment}
              onCancel={handleCancelReply}
              availableUsers={commenters}
              compact={true}
            />
          </div>
        )}

        {/* Nested comments - Using replies array directly from the comment object */}
        {comment.replies && comment.replies.length > 0 && (
          <ul className="mt-4 space-y-5 ml-6">
            {comment.replies.map((reply) => (
              <div
                key={reply.id}
                id={`comment-${reply.id}`}
                className="transition-colors duration-500"
              >
                <CommentItem
                  comment={reply}
                  onReply={onReply}
                  isReplyActive={isReplyActive}
                  onMention={onMention}
                  getCommentsByParentId={getCommentsByParentId}
                  getLimitedComments={getLimitedComments}
                  hasMoreComments={hasMoreComments}
                  loadMoreComments={loadMoreComments}
                  nestingLevel={nestingLevel + 1} // Increment nesting level for replies
                />
              </div>
            ))}
          </ul>
        )}

        {/* Show nested comments from the flat structure if any (backward compatibility) */}
        {(!comment.replies || comment.replies.length === 0) &&
          getLimitedComments(comment.id).length > 0 && (
            <ul className="mt-4 space-y-5 ml-6">
              {getLimitedComments(comment.id).map((reply) => (
                <div
                  key={reply.id}
                  id={`comment-${reply.id}`}
                  className="transition-colors duration-500"
                >
                  <CommentItem
                    comment={reply}
                    onReply={onReply}
                    isReplyActive={isReplyActive}
                    onMention={onMention}
                    getCommentsByParentId={getCommentsByParentId}
                    getLimitedComments={getLimitedComments}
                    hasMoreComments={hasMoreComments}
                    loadMoreComments={loadMoreComments}
                    nestingLevel={nestingLevel + 1} // Increment nesting level for replies
                  />
                </div>
              ))}
              {hasMoreComments(comment.id) && (
                <div className="mt-1 text-center">
                  <button
                    className={`text-sm font-medium ${theme.text.brand} ${theme.text.brandHover}`}
                    onClick={() => loadMoreComments(comment.id)}
                  >
                    Show more replies
                  </button>
                </div>
              )}
            </ul>
          )}
      </li>
      {/* Delete confirmation modal */}
      {showDeleteConfirmation && (
        <ConfirmationModal
          isOpen={showDeleteConfirmation}
          title="Delete Comment"
          message="Are you sure you want to delete this comment? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          onConfirm={handleDeleteConfirm}
          onClose={handleDeleteCancel}
          isLoading={isDeleting}
        />
      )}
    </>
  );
}

export default CommentItem;
